import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, input, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { MediaKind } from './media-api';
import { formatSize, pageLang } from './media-format';
import { FrameDialogData, MediaFrameDialogComponent } from './media-frame-dialog.component';
import { MediaIconComponent } from './media-icon.component';
import { MediaLeaveGuard } from './media-leave-guard';
import { MediaPreviewCache } from './media-preview-cache';
import { acceptFor } from './media-sniff';
import { MediaPreviewCardComponent } from './media-preview-card.component';
import { MediaTileComponent } from './media-tile.component';
import { Tile } from './media-tiles';
import { MediaUploader } from './media-uploader';
import { ProductMediaState, Sibling } from './product-media.state';

/** What the file input is picking for: new items, the replacement of an item, or the photo that takes the place of the old one. */
interface PickMode {
  replace?: { id: number; kind: MediaKind };
  front?: boolean;
}

interface Row {
  tile: Tile;
  draggable: boolean;
  last: boolean;
  busy: boolean;
}

/**
 * The tab «Фото и видео» of a size (the boards AdmMediaEditor, AdmMediaCrop, AdmMediaStates): up to 5 photos and 1 video in the order they are shown, the first is the main.
 * Everything is saved at once; a deletion can be taken back for ten seconds. The page only draws and passes what the editor does to `ProductMediaState`.
 */
@Component({
  selector: 'hc-product-media',
  imports: [
    TranslocoDirective,
    NzButtonComponent,
    NzDropDownDirective,
    NzDropdownMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    NzSkeletonComponent,
    MediaIconComponent,
    MediaPreviewCardComponent,
    MediaTileComponent
  ],
  providers: [ProductMediaState, MediaUploader, MediaPreviewCache],
  templateUrl: './product-media-tab.component.html',
  styleUrls: ['./product-media-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductMediaTabComponent implements OnInit, OnDestroy {
  /** The size (`hc_product_info`) whose photos and video these are. */
  readonly productInfoId = input.required<number>();

  protected readonly state = inject(ProductMediaState);
  private readonly modal = inject(NzModalService);
  private readonly translate = inject(TranslocoService);
  private readonly leave = inject(MediaLeaveGuard);
  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  private mode: PickMode = {};
  private unregisterLeave: () => void = () => undefined;

  protected readonly dragFrom = signal<number | null>(null);
  protected readonly dragOver = signal<number | null>(null);
  protected readonly dropFiles = signal(false);
  protected readonly legacyBroken = signal(false);
  protected readonly copyOpen = signal(false);

  protected readonly rows = computed<Row[]>(() => {
    const tiles = this.state.tiles();
    const editable = this.state.editable();
    const orderBusy = this.state.orderBusy();
    const lastMovable = tiles.reduce((last, t) => (t.id !== null ? t.index : last), 0);
    return tiles.map(tile => ({
      tile,
      draggable: editable && !orderBusy && tile.id !== null && (tile.state === 'ready' || tile.state === 'processing' || tile.state === 'delayed' || tile.state === 'failed'),
      last: tile.index >= lastMovable,
      busy: this.state.isBusy(tile.id) || (tile.replacement?.id != null && this.state.isBusy(tile.replacement.id))
    }));
  });

  protected readonly photoFree = computed(() => this.state.counts().photos < this.state.limits().photos);
  protected readonly videoFree = computed(() => this.state.counts().videos < this.state.limits().videos);
  protected readonly freePhotos = computed(() => this.state.limits().photos - this.state.counts().photos);
  protected readonly freeKind = computed<'both' | 'photo' | 'video' | null>(() => {
    if (this.photoFree() && this.videoFree()) {
      return 'both';
    }
    return this.photoFree() ? 'photo' : this.videoFree() ? 'video' : null;
  });
  protected readonly rules = computed(() => {
    const l = this.state.limits();
    const lang = pageLang(this.translate.getActiveLang());
    return {
      photoMax: formatSize(l.photoBytes, lang),
      videoMax: formatSize(l.videoBytes, lang),
      photos: l.photos,
      good: l.photoGoodSide,
      min: l.photoMinSide,
      seconds: l.videoSeconds,
      minSide: l.videoMinSide
    };
  });
  /** The other sizes of the cake for the list «Взять из другого размера», each with its line of numbers. */
  protected readonly siblingRows = computed(() =>
    (this.state.siblings() ?? []).map(s => {
      let sub: string;
      if (s.ready === 0) {
        sub = this.state.t('copy.sub.processing');
      } else if (s.videos > 0 && s.photos > 0) {
        // «N фото и видео»: N counts the photos (the video is the «и видео»)
        sub = this.state.t('copy.sub.both', { n: s.photos });
      } else if (s.videos > 0) {
        sub = this.state.t('copy.sub.video');
      } else {
        sub = this.state.t('copy.sub.photos', { n: s.photos });
      }
      return { ...s, sub };
    })
  );
  protected readonly working = computed(() => this.state.orderBusy() || this.state.copyBusy() || this.state.busy().size > 0);
  protected readonly copyDisabled = computed(() => !this.state.editable() || this.state.uploader.active || this.state.copyBusy());

  ngOnInit(): void {
    // leaving the page while a file is going up is asked about (route guard); nothing is asked otherwise
    this.unregisterLeave = this.leave.register(() => this.state.uploader.active);
    void this.state.init(this.productInfoId());
  }

  ngOnDestroy(): void {
    this.unregisterLeave();
    this.state.destroy();
  }

  /** The tab is closed with files on their way: the browser asks first. */
  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.state.uploader.active) {
      event.preventDefault();
    }
  }

  /** The tab is closed with deletions that wait: they are sent in a way that outlives the page. */
  @HostListener('window:pagehide')
  protected onPageHide(): void {
    this.state.flushOnUnload();
  }

  /** The page comes back from the back/forward cache: what it sent on the way out is done; the gallery is read anew. */
  @HostListener('window:pageshow', ['$event'])
  protected onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.state.restoredFromCache();
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ files

  /** Opens the file dialog for new items (what is still free is offered), or for a replacement, or for the photo that takes the place of the old one. */
  protected pick(mode: PickMode = {}): void {
    this.mode = mode;
    const input = this.fileInput().nativeElement;
    input.multiple = !mode.replace && !mode.front;
    input.accept = mode.replace ? acceptFor(mode.replace.kind === 'PHOTO', mode.replace.kind === 'VIDEO') : mode.front ? acceptFor(true, false) : acceptFor(this.photoFree(), this.videoFree());
    input.value = '';
    input.click();
  }

  protected onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    void this.state.pickFiles(files, this.mode);
  }

  protected onFileDragOver(event: DragEvent): void {
    if (this.dragFrom() === null && event.dataTransfer?.types.includes('Files') && this.state.editable()) {
      event.preventDefault();
      this.dropFiles.set(true);
    }
  }

  protected onFileDragLeave(): void {
    this.dropFiles.set(false);
  }

  protected onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.dropFiles.set(false);
    if (this.state.editable()) {
      void this.state.pickFiles(Array.from(event.dataTransfer?.files ?? []), {});
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ order by drag

  protected onDragStart(event: DragEvent, index: number): void {
    this.dragFrom.set(index);
    try {
      event.dataTransfer?.setData('text/plain', `hc-media:${index}`);
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
      }
    } catch {
      // some browsers refuse to set data on a drag that starts from a nested element
    }
  }

  protected onTileDragOver(event: DragEvent, index: number): void {
    if (this.dragFrom() !== null) {
      event.preventDefault();
      if (this.dragOver() !== index) {
        this.dragOver.set(index);
      }
    }
  }

  protected onTileDragLeave(index: number): void {
    if (this.dragOver() === index) {
      this.dragOver.set(null);
    }
  }

  protected onTileDrop(event: DragEvent, index: number): void {
    const from = this.dragFrom();
    if (from !== null) {
      event.preventDefault();
      this.state.moveVisible(from, index);
    }
    this.endDrag();
  }

  protected endDrag(): void {
    this.dragFrom.set(null);
    this.dragOver.set(null);
  }

  // ------------------------------------------------------------------------------------------------------------------------------ tile actions

  protected onFrame(tile: Tile): void {
    if (!tile.canFrame || tile.id === null) {
      return;
    }
    const total = this.state.tiles().length;
    this.modal.create<MediaFrameDialogComponent, FrameDialogData>({
      nzTitle: this.state.t(tile.kind === 'VIDEO' ? 'frame.title.VIDEO' : 'frame.title.PHOTO', { n: tile.index + 1, total }),
      nzContent: MediaFrameDialogComponent,
      nzData: { state: this.state, itemId: tile.id },
      nzWidth: 1140,
      nzFooter: null,
      nzMaskClosable: false,
      nzCentered: false
    });
  }

  /** The main one is asked about: the picture of the catalog and the cart changes. Any other goes at once, with the ten seconds to take it back. */
  protected onRemove(tile: Tile): void {
    if (tile.id === null) {
      return;
    }
    const id = tile.id;
    if (!tile.main || tile.state === 'failed' || tile.state === 'interrupted') {
      this.state.remove(id);
      return;
    }
    const next = this.state.tiles()[1];
    const consequence = next ? this.state.t(next.kind === 'VIDEO' ? 'confirmMain.nextVideo' : 'confirmMain.nextPhoto', { n: 2 }) : this.state.t('confirmMain.none');
    this.modal.confirm({
      nzTitle: this.state.t(tile.kind === 'VIDEO' ? 'confirmMain.titleVideo' : 'confirmMain.titlePhoto'),
      nzContent: consequence,
      nzOkText: this.state.t('confirmMain.ok'),
      nzOkDanger: true,
      nzCancelText: this.state.t('confirmMain.cancel'),
      nzOnOk: () => this.state.remove(id)
    });
  }

  protected onReplace(tile: Tile): void {
    if (tile.id !== null) {
      this.pick({ replace: { id: tile.id, kind: tile.kind } });
    }
  }

  protected onMove(tile: Tile, direction: -1 | 1): void {
    if (tile.id !== null) {
      this.state.step(tile.id, direction);
    }
  }

  protected onMakeMain(tile: Tile): void {
    if (tile.id !== null) {
      this.state.makeMain(tile.id);
    }
  }

  protected onRetry(tile: Tile): void {
    if (tile.id !== null) {
      void this.state.retry(tile.id);
    }
  }

  protected onRetryUpload(tile: Tile): void {
    if (tile.uid !== null) {
      void this.state.retryUpload(tile.uid);
    }
  }

  protected onCancel(tile: Tile): void {
    if (tile.uid !== null) {
      void this.state.cancelUpload(tile.uid);
    }
  }

  protected onRetryReplacement(tile: Tile): void {
    if (tile.replacement?.id != null) {
      void this.state.retry(tile.replacement.id);
    }
  }

  protected onCancelReplacement(tile: Tile): void {
    const item = this.state.list()?.items.find(i => i.id === tile.id);
    if (item) {
      void this.state.cancelReplacement(item);
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ copy

  protected onCopyVisible(open: boolean): void {
    this.copyOpen.set(open);
    if (open) {
      void this.state.loadSiblings();
    }
  }

  protected onCopy(sibling: Sibling): void {
    if (sibling.ready === 0 || this.copyDisabled()) {
      return;
    }
    const own = this.state.counts();
    const ownTotal = own.photos + own.videos;
    if (ownTotal === 0) {
      void this.state.copyFrom(sibling);
      return;
    }
    this.modal.confirm({
      nzTitle: this.state.t('copy.confirmTitle', { title: sibling.title }),
      nzContent: this.state.t('copy.confirmText', { own: ownTotal, other: sibling.photos + sibling.videos }),
      nzOkText: this.state.t('copy.ok'),
      nzCancelText: this.state.t('copy.cancel'),
      nzOnOk: () => this.state.copyFrom(sibling)
    });
  }

  protected onUndo(id: number): void {
    this.state.undo(id);
  }

  protected reload(): void {
    this.state.loading.set(true);
    void this.state.init(this.productInfoId());
  }
}
